import React from "react";
import ReactDOM from "react-dom/client";
import { FlareErrorBoundary } from "@flareapp/react";
import AppLayout from "./layouts/app-layout";
import { initFlare } from "@/lib/flareInit";
import { initChunkReload } from "@/lib/chunkReload";
import { captureSignupToken } from "@/lib/signupToken";
import { bootAuthSession, initSessionSync } from "@/lib/authSession";
import { initSessionQuerySync } from "@/lib/sessionQuerySync";
import { initIdleSession } from "@/lib/idleSession";
import { isDevOrStaging } from "@/lib/analytics";
import { redactEventCredentials } from "@/lib/redactUrlCredentials";
import { redactSensitiveUrls } from "@/lib/posthogRedaction";
import posthog from "posthog-js";
import { PostHogProvider } from "@posthog/react";

// Only init PostHog in production with a real token: keeps local/staging/QA
// emails out of the prod project (mirrors analytics.ts dev segregation), and
// avoids initializing against an empty project. session_recording masking only
// affects replay DOM capture (not capture() payloads): maskAllInputs hides form
// values; maskTextSelector hides any element tagged `.ph-mask` (opt-in — add the
// class to sensitive text nodes to mask them if replay is ever enabled).
// recordBody: false keeps network bodies out of replay on purpose, whatever the
// project's network capture setting says (a client false wins over the remote
// one): secret reveals return keys in fields PostHog's payload scrubbing does not
// catch (a Click 2 Call key comes back as `key`).
// cross_subdomain_cookie writes the cookie at the registrable domain (.pbx.im)
// so the anonymous device id carries across pbx.im (website) → app.pbx.im
// (signup) → tenant.pbx.im, stitching the acquisition funnel into one person.
const posthogToken = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN;
if (posthogToken && !isDevOrStaging()) {
    posthog.init(posthogToken, {
        api_host: import.meta.env.VITE_POSTHOG_HOST,
        defaults: "2026-01-30",
        cross_subdomain_cookie: true,
        // Our emailed links carry a CREDENTIAL in the query (reset/invitation `token`, email-change
        // `code`), and a Click 2 Call link the number it dials (`destination`): posthog-js copies the URL into
        // many properties, so redact every string in the event, then the URL-shaped ones in any encoding.
        before_send: (event) => (event ? redactSensitiveUrls(redactEventCredentials(event)) : event),
        session_recording: {
            maskAllInputs: true,
            maskTextSelector: ".ph-mask",
            recordBody: false,
        },
    });
}

initFlare();
initChunkReload();
captureSignupToken();

// CT-36 silent-refresh boot: exchange the __Host-pbx_rt cookie for an in-memory access token
// before anything protected paints. Kicked off pre-render so the round-trip overlaps React
// bootstrapping; RootRedirect/DashboardLayoutWrapper hold on authStore.bootStatus.
void bootAuthSession();

// Cross-tab logout + the suspend/resume rotation watchdog, and the scope-probe cache refresh
// that follows every new token. Registered before render so a logout broadcast arriving during
// bootstrap is not missed.
initSessionSync();
initSessionQuerySync();
initIdleSession();

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <PostHogProvider client={posthog}>
            <FlareErrorBoundary>
                <AppLayout />
            </FlareErrorBoundary>
        </PostHogProvider>
    </React.StrictMode>
);
